import { APP_DEEP_LINKS, APP_STORE_URLS, UNIVERSAL_LINK } from '../hooks/useMobileAppDetection';

interface RedirectOptions {
    fallbackDelay?: number; // Delay before showing fallback (default: 2.5 seconds)
    showFallback?: boolean; // Whether to show fallback UI (default: true)
    onAppOpen?: () => void; // Callback when app opens
    onFallback?: () => void; // Callback when fallback is shown
}

class MobileAppRedirectService {
    private redirectTimeout: NodeJS.Timeout | null = null;
    private hasRedirected = false;

    /**
     * Attempts to open the mobile app or redirect to app store
     */
    async redirectToMobileApp(
        isIOS: boolean,
        isAndroid: boolean,
        options: RedirectOptions = {}
    ): Promise<void> {
        const {
            fallbackDelay = 2500,
            showFallback = true,
            onAppOpen,
            onFallback,
        } = options;

        if (this.hasRedirected) {
            return;
        }

        this.hasRedirected = true;

        try {
            if (isIOS) {
                await this.handleIOSRedirect(fallbackDelay, showFallback, onAppOpen, onFallback);
            } else if (isAndroid) {
                await this.handleAndroidRedirect(fallbackDelay, showFallback, onAppOpen, onFallback);
            }
        } catch (error) {
            console.error('Mobile app redirect failed:', error);
            this.handleFallback(isIOS, onFallback);
        }
    }

    private async handleIOSRedirect(
        fallbackDelay: number,
        showFallback: boolean,
        onAppOpen?: () => void,
        onFallback?: () => void
    ): Promise<void> {
        // Try universal link first (if available)
        if (UNIVERSAL_LINK) {
            const universalLinkSuccess = await this.tryUniversalLink(UNIVERSAL_LINK);
            if (universalLinkSuccess) {
                onAppOpen?.();
                return;
            }
        }

        // Try deep link
        const deepLinkSuccess = await this.tryDeepLink(APP_DEEP_LINKS.ios);
        if (deepLinkSuccess) {
            onAppOpen?.();
            return;
        }

        // Set up fallback
        if (showFallback) {
            this.redirectTimeout = setTimeout(() => {
                this.handleFallback(true, onFallback);
            }, fallbackDelay);
        }
    }

    private async handleAndroidRedirect(
        fallbackDelay: number,
        showFallback: boolean,
        onAppOpen?: () => void,
        onFallback?: () => void
    ): Promise<void> {
        // Try deep link
        const deepLinkSuccess = await this.tryDeepLink(APP_DEEP_LINKS.android);
        if (deepLinkSuccess) {
            onAppOpen?.();
            return;
        }

        // Set up fallback
        if (showFallback) {
            this.redirectTimeout = setTimeout(() => {
                this.handleFallback(false, onFallback);
            }, fallbackDelay);
        }
    }

    private async tryUniversalLink(url: string): Promise<boolean> {
        return new Promise((resolve) => {
            const startTime = Date.now();
            
            // Create a hidden iframe to test universal link
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);

            // Listen for page visibility changes (indicates app switch)
            const handleVisibilityChange = () => {
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) { // If visibility changed quickly, app likely opened
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                    document.body.removeChild(iframe);
                    resolve(true);
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);

            // Cleanup after timeout
            setTimeout(() => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                }
                resolve(false);
            }, 2000);
        });
    }

    private async tryDeepLink(url: string): Promise<boolean> {
        return new Promise((resolve) => {
            const startTime = Date.now();
            
            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.style.display = 'none';
            document.body.appendChild(link);

            // Listen for page visibility changes
            const handleVisibilityChange = () => {
                const elapsed = Date.now() - startTime;
                if (elapsed < 2000) {
                    document.removeEventListener('visibilitychange', handleVisibilityChange);
                    document.body.removeChild(link);
                    resolve(true);
                }
            };

            document.addEventListener('visibilitychange', handleVisibilityChange);

            // Trigger the deep link
            link.click();

            // Cleanup after timeout
            setTimeout(() => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                if (document.body.contains(link)) {
                    document.body.removeChild(link);
                }
                resolve(false);
            }, 2000);
        });
    }

    private handleFallback(isIOS: boolean, onFallback?: () => void): void {
        const storeUrl = isIOS ? APP_STORE_URLS.ios : APP_STORE_URLS.android;
        
        // Open app store in new tab
        window.open(storeUrl, '_blank');
        
        onFallback?.();
    }

    /**
     * Cancel any pending redirect
     */
    cancelRedirect(): void {
        if (this.redirectTimeout) {
            clearTimeout(this.redirectTimeout);
            this.redirectTimeout = null;
        }
        this.hasRedirected = false;
    }

    /**
     * Check if device is likely to have the app installed
     */
    isAppLikelyInstalled(): boolean {
        // This is a heuristic - you might want to implement more sophisticated detection
        // For example, checking localStorage for app-specific data, or using other indicators
        return localStorage.getItem('idearify-app-installed') === 'true';
    }

    /**
     * Mark app as installed (call this from your mobile app)
     */
    markAppAsInstalled(): void {
        localStorage.setItem('idearify-app-installed', 'true');
    }
}

export const mobileAppRedirectService = new MobileAppRedirectService();
